import clsx from "clsx";
import { useEffect, useState, useId } from "react";
import * as d3 from "d3";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { EndNotes } from "@/components/EndNotes";
import { PageContainer } from "@/components/PageContainer";
import { ChartHeader } from "@/components/ChartHeader";
import { Select } from "@/components/Select";
import { NoData } from "@/components/NoData";
import economyOptions from "@/config/interdependencies/economies";
import sectorOptions from "@/config/interdependencies/sectors";
import { getSectorItems, getEconomyItems } from "@/utils/filter";
import {
  formatMoney,
  formatMoneySr,
  makePossessive,
  getSubSectorDisplayName,
  formatPercentage,
  getEconomyDisplayName,
  isOtherSubSectorCode,
  formatRows,
} from "@/utils/format";
import { useFilter } from "@/hooks/useFilter";
import { Donut } from "./Donut";
import { useUrls } from "@/hooks/useUrls";
import styles from "./styles.module.scss";

const economyItems = getEconomyItems(economyOptions);

const sectorItems = getSectorItems(sectorOptions);

const schema = [
  { name: "Year" },
  { name: "Partner code" },
  { name: "Sub-sector code" },
  {
    name: "Value",
    format: (value: string) => parseInt(value),
  },
  {
    name: "Value of reporter imports",
    format: (value: string) => parseInt(value),
  },
  {
    name: "Value of partner exports",
    format: (value: string) => parseInt(value),
  },
] as const;

const getDataRows = (rows: unknown) => {
  return formatRows(rows, schema);
};

type DataRow = ReturnType<typeof getDataRows>[number];

export function App() {
  const urls = useUrls();
  const pageSlug = "interdependencies";
  const urlInfo = urls[pageSlug] || {};

  const [data, setData] = useState<DataRow[]>([]);
  const [data2, setData2] = useState<DataRow[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoadingData2, setIsLoadingData2] = useState(true);

  const { value: economyDisplay, onChange: onEconomyDisplayChange } = useFilter(
    "eco",
    economyOptions,
    "usa"
  );

  // Use a version of economy lagging behind the filter value to wait until
  // we have the new data set to render donuts to prevent accessing rows that
  // don't exist.
  const [economy, setEconomy] = useState(economyDisplay);

  const { value: sector, onChange: onSectorChange } = useFilter(
    "sector",
    sectorOptions
  );

  const yearOptionsSet = new Set<string>();
  data.forEach((row) => {
    yearOptionsSet.add(row.Year);
  });
  const yearOptions = data.length
    ? Array.from(yearOptionsSet).sort((a, b) => parseInt(b) - parseInt(a))
    : [];

  const { value: year, onChange: onYearChange } = useFilter(
    "year",
    yearOptions
  );

  const economy2Set = new Set<string>();
  data.forEach((row) => {
    economy2Set.add(row["Partner code"].toLowerCase());
  });
  const economy2Options = data.length
    ? Array.from(economy2Set).sort((a, b) =>
        getEconomyDisplayName(a).localeCompare(getEconomyDisplayName(b))
      )
    : [];
  const economy2Items = getEconomyItems(economy2Options);

  const { value: economy2Display, onChange: onEconomy2DisplayChange } =
    useFilter("eco2", economy2Options, "chn");

  // Use a version of economy lagging behind the filter value to wait until
  // we have the new data set to render donuts to prevent accessing rows that
  // don't exist.
  const [economy2, setEconomy2] = useState(economy2Display);

  useEffect(() => {
    setIsLoadingData(true);
    d3.csv(
      `${
        process.env.ASSET_PATH || ""
      }/data/interdependencies/${economyDisplay}-${sector}.csv`
    )
      .then((data) => {
        setData(getDataRows(data));
      })
      .catch((e) => {
        console.error(e);
        setData([]);
      })
      .finally(() => {
        setIsLoadingData(false);
        setEconomy(economyDisplay);
      });
  }, [economyDisplay, sector]);

  useEffect(() => {
    if (economy2Display) {
      setIsLoadingData2(true);
      d3.csv(
        `${
          process.env.ASSET_PATH || ""
        }/data/interdependencies/${economy2Display}-${sector}.csv`
      )
        .then((data) => {
          setData2(getDataRows(data));
        })
        .catch((e) => {
          console.error(e);
          setData2([]);
        })
        .finally(() => {
          setIsLoadingData2(false);
          setEconomy2(economy2Display);
        });
    }
  }, [economy2Display, sector]);

  const formattedEconomy = getEconomyDisplayName(economy);
  const formattedEconomy2 = getEconomyDisplayName(economy2);

  // Default sort
  const [rowSort, setRowSort] = useState<
    "economyImports" | "economyExports" | "partnerImports" | "partnerExports"
  >("economyImports");

  const filteredData = data.filter(
    (row) =>
      row["Partner code"] === economy2?.toUpperCase() && row.Year === year
  );

  const filteredData2 = data2.filter(
    (row) => row["Partner code"] === economy.toUpperCase() && row.Year === year
  );

  const combinedData = [
    ...filteredData.map((row) => ({
      row1: row,
      row2: filteredData2.find(
        (r) => r["Sub-sector code"] === row["Sub-sector code"]
      ),
    })),
    ...filteredData2
      .filter(
        (row) =>
          !filteredData.some(
            (r) => r["Sub-sector code"] === row["Sub-sector code"]
          )
      )
      .map((row) => ({
        row1: undefined,
        row2: row,
      })),
  ];

  const sortedData = combinedData.sort((a, b) => {
    const { row1: aRow1, row2: aRow2 } = a;
    const { row1: bRow1, row2: bRow2 } = b;

    if (aRow1 && isOtherSubSectorCode(aRow1["Sub-sector code"])) {
      return 1;
    }

    if (bRow1 && isOtherSubSectorCode(bRow1["Sub-sector code"])) {
      return -1;
    }

    // Col 1
    if (rowSort === "economyImports" && aRow1 && bRow1) {
      return (
        bRow1.Value / bRow1["Value of reporter imports"] -
        aRow1.Value / aRow1["Value of reporter imports"]
      );
    }
    // Col 2
    if (rowSort === "economyExports" && aRow2 && bRow2) {
      return (
        bRow2.Value / bRow2["Value of partner exports"] -
        aRow2.Value / aRow2["Value of partner exports"]
      );
    }
    // Col 3
    if (rowSort === "partnerImports" && aRow2 && bRow2) {
      return (
        bRow2.Value / bRow2["Value of reporter imports"] -
        aRow2.Value / aRow2["Value of reporter imports"]
      );
    }
    // Col 4
    if (rowSort === "partnerExports" && aRow1 && bRow1) {
      return (
        bRow1.Value / bRow1["Value of partner exports"] -
        aRow1.Value / aRow1["Value of partner exports"]
      );
    }

    return 0;
  });

  const sortLabelId = useId();

  return (
    <PageContainer
      headline={urlInfo.headline}
      dek={urlInfo.dek}
      footerCTASlug="product-concentration"
      activePageSlug="interdependencies"
      isLoading={isLoadingData || isLoadingData2}
      filters={
        <>
          <Select
            label="Economy"
            items={economyItems}
            value={economyDisplay}
            format="control-bar"
            onChange={(value) => {
              onEconomyDisplayChange(value);
            }}
          />
          <Select
            label="Trading partner"
            items={economy2Items}
            value={economy2Display}
            format="control-bar"
            onChange={(value) => {
              onEconomy2DisplayChange(value);
            }}
          />
          <Select
            label="Sector"
            items={sectorItems}
            value={sector}
            format="control-bar"
            onChange={(value) => {
              onSectorChange(value);
            }}
          />
          <Select
            label="Year"
            items={yearOptions.map((o) => ({ value: o }))}
            value={year}
            format="control-bar"
            onChange={(value) => onYearChange(value)}
          />
        </>
      }
    >
      <div className={clsx(styles[`chart-wrap`])}>
        <ChartHeader
          title={`${formattedEconomy} and ${formattedEconomy2} trade interdependencies`}
          description={`See how much ${formattedEconomy} and ${formattedEconomy2} buy and sell from each other in different sectors in ${year}.`}
        />

        <header className={styles.header}>
          <p className={styles["header-instructions"]} id={sortLabelId}>
            Select a heading to sort the&nbsp;rows
          </p>

          <ToggleGroup.Root
            type="single"
            onValueChange={(v) => {
              setRowSort(v as typeof rowSort);
            }}
            value={rowSort}
            aria-labelledby={sortLabelId}
            className={styles["header-buttons"]}
          >
            <ToggleGroup.Item
              value="economyImports"
              className={styles["header-btn"]}
            >
              How much {formattedEconomy} buys from&nbsp;
              {formattedEconomy2}
            </ToggleGroup.Item>
            <ToggleGroup.Item
              value="economyExports"
              className={styles["header-btn"]}
            >
              How much {formattedEconomy} sells to&nbsp;
              {formattedEconomy2}
            </ToggleGroup.Item>
            <ToggleGroup.Item
              value="partnerImports"
              className={styles["header-btn"]}
            >
              How much {formattedEconomy2} buys from&nbsp;
              {formattedEconomy}
            </ToggleGroup.Item>
            <ToggleGroup.Item
              value="partnerExports"
              className={styles["header-btn"]}
            >
              How much {formattedEconomy2} sells to&nbsp;
              {formattedEconomy}
            </ToggleGroup.Item>
          </ToggleGroup.Root>
        </header>

        {!sortedData.length ? (
          <NoData />
        ) : (
          sortedData.map((row) => {
            const { row1, row2 } = row;
            const subSectorCode =
              row1?.["Sub-sector code"] || row2?.["Sub-sector code"];

            return (
              <div key={subSectorCode} className={styles["sector-group"]}>
                <h3 className={styles["sector-name"]}>
                  {getSubSectorDisplayName(subSectorCode)}
                </h3>
                <div className={styles["chart-row"]}>
                  <Donut
                    economyClass="economy-a"
                    percent={
                      row1
                        ? row1.Value / row1["Value of reporter imports"]
                        : undefined
                    }
                    description={
                      row1 ? (
                        <>
                          {formatMoney(row1.Value)} of{" "}
                          {makePossessive(formattedEconomy)}{" "}
                          {formatMoney(row1["Value of reporter imports"])}{" "}
                          total&nbsp;imports
                        </>
                      ) : (
                        "No data"
                      )
                    }
                    srDescription={
                      row1
                        ? `${formattedEconomy} buys ${formatPercentage(
                            row1.Value / row1["Value of reporter imports"]
                          )} or ${formatMoneySr(
                            row1.Value
                          )} of its ${formatMoneySr(
                            row1["Value of reporter imports"]
                          )} total imports from ${formattedEconomy2}.`
                        : `No data for how much of ${makePossessive(
                            formattedEconomy
                          )} total imports are bought from ${formattedEconomy2}.`
                    }
                  />
                  <Donut
                    economyClass="economy-a"
                    percent={
                      row2
                        ? row2.Value / row2["Value of partner exports"]
                        : undefined
                    }
                    description={
                      row2 ? (
                        <>
                          {formatMoney(row2.Value)} of{" "}
                          {makePossessive(formattedEconomy)}{" "}
                          {formatMoney(row2["Value of partner exports"])}{" "}
                          total&nbsp;exports
                        </>
                      ) : (
                        "No data"
                      )
                    }
                    srDescription={
                      row2
                        ? `${formattedEconomy} sells ${formatPercentage(
                            row2.Value / row2["Value of partner exports"]
                          )} or ${formatMoneySr(
                            row2.Value
                          )} of its ${formatMoneySr(
                            row2["Value of partner exports"]
                          )} total exports to ${formattedEconomy2}.`
                        : `No data for how much of ${makePossessive(
                            formattedEconomy
                          )} total exports are sold to ${formattedEconomy2}.`
                    }
                  />
                  <Donut
                    economyClass="economy-b"
                    percent={
                      row2
                        ? row2.Value / row2["Value of reporter imports"]
                        : undefined
                    }
                    description={
                      row2 ? (
                        <>
                          {formatMoney(row2.Value)} of{" "}
                          {makePossessive(formattedEconomy2)}{" "}
                          {formatMoney(row2["Value of reporter imports"])}{" "}
                          total&nbsp;imports
                        </>
                      ) : (
                        "No data"
                      )
                    }
                    srDescription={
                      row2
                        ? `${formattedEconomy2} buys ${formatPercentage(
                            row2.Value / row2["Value of reporter imports"]
                          )} or ${formatMoneySr(
                            row2.Value
                          )} of its ${formatMoneySr(
                            row2["Value of reporter imports"]
                          )} total imports from ${formattedEconomy}.`
                        : `No data for how much of ${makePossessive(
                            formattedEconomy2
                          )} total imports are bought from ${formattedEconomy}.`
                    }
                  />
                  <Donut
                    economyClass="economy-b"
                    percent={
                      row1
                        ? row1.Value / row1["Value of partner exports"]
                        : undefined
                    }
                    description={
                      row1 ? (
                        <>
                          {formatMoney(row1.Value)} of{" "}
                          {makePossessive(formattedEconomy2)}{" "}
                          {formatMoney(row1["Value of partner exports"])}{" "}
                          total&nbsp;exports
                        </>
                      ) : (
                        "No data"
                      )
                    }
                    srDescription={
                      row1
                        ? `${formattedEconomy2} sells ${formatPercentage(
                            row1.Value / row1["Value of partner exports"]
                          )} or ${formatMoneySr(
                            row1.Value
                          )} of its ${formatMoneySr(
                            row1["Value of partner exports"]
                          )} total exports to ${formattedEconomy}.`
                        : `No data for how much of ${makePossessive(
                            formattedEconomy2
                          )} total exports are sold to ${formattedEconomy}.`
                    }
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className={styles["notes-container"]}>
        <EndNotes mode="collapsed">
          <p>
            Trade on a regional level excludes trade between countries belonging
            to the same region. Flows are calculated using only UN Comtrade
            import data; displayed export values may differ from other
            visualizations that use both import and export data, as there are
            asymmetries in the UN Comtrade import and export datasets.
          </p>
          <p>Source: UN Comtrade (Refreshed 2025-01-30)</p>
        </EndNotes>
      </div>
    </PageContainer>
  );
}
